import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Lightbox from "@/components/feature/Lightbox";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import ShareButton from "@/pages/detail/components/ShareButton";
import { getMediaById, getRelated, typeLabel } from "@/lib/catalog";

export default function Detail() {
  const { id } = useParams();
  const item = id ? getMediaById(id) : undefined;
  const [lightbox, setLightbox] = useState<number | null>(null);

  const related = useMemo(() => (item ? getRelated(item, 4) : []), [item]);

  const gallery = useMemo(() => {
    if (!item) return [];
    const images = [item.backdrop, item.cover, ...related.slice(0, 2).map((r) => r.backdrop)].filter(
      Boolean,
    );
    return images;
  }, [item, related]);

  useSeo({
    title: item
      ? `${item.title} - ${typeLabel(item.type)}详情 - 金桔影院`
      : "影片详情 - 金桔影院",
    description: item
      ? `${item.title}是由${item.director}执导，${item.cast.slice(0, 3).join("、")}主演的${item.genres.join("、")}${typeLabel(item.type)}。${item.synopsis.slice(0, 80)}`
      : "金桔影院影视作品详情页。",
    keywords: item
      ? `金桔影院,${item.title},${item.genres.join(",")},${item.region}${typeLabel(item.type)},剧情介绍`
      : "金桔影院,影片详情",
    canonicalPath: item ? `/detail/${item.id}` : "/",
  });

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-8xl px-4 py-20 md:px-6">
        <EmptyState
          title="没有找到这部作品"
          description="它可能已下架或链接有误，去分类页看看其他推荐吧。"
        />
      </div>
    );
  }

  const typeText = typeLabel(item.type);

  return (
    <article>
      <section className="relative overflow-hidden bg-background-950">
        <div className="absolute inset-0">
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 金桔影院`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/80 to-background-950/60"></div>
        </div>

        <div className="relative mx-auto w-full max-w-8xl px-4 py-8 md:px-6 md:py-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-200 md:text-sm">
            <Link to="/" className="transition-colors hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${item.type}`} className="transition-colors hover:text-primary-300">
              {typeText}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-semibold text-primary-300">{item.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="mx-auto w-40 shrink-0 overflow-hidden rounded-lg bg-background-800 md:mx-0 md:w-52">
              <img
                src={item.cover}
                alt={`${item.title} 海报`}
                title={`${item.title} - 金桔影院`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-3xl font-extrabold text-background-50 md:text-4xl">
                {item.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-2.5 text-sm">
                <span className="flex items-center gap-1 rounded-md bg-primary-500 px-2.5 py-1 font-bold text-background-50">
                  <i className="ri-star-fill text-xs"></i>
                  {item.rating.toFixed(1)}
                </span>
                <span className="rounded-md border border-background-100/30 px-2.5 py-1 text-background-100">
                  {typeText}
                </span>
                <span className="rounded-md border border-background-100/30 px-2.5 py-1 text-background-100">
                  {item.year}
                </span>
                <span className="rounded-md border border-background-100/30 px-2.5 py-1 text-background-100">
                  {item.region}
                </span>
                <span className="rounded-md border border-background-100/30 px-2.5 py-1 text-background-100">
                  {item.duration}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {item.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${item.type}`}
                    className="rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold text-background-50 transition-colors hover:bg-accent-600"
                  >
                    {genre}
                  </Link>
                ))}
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-background-50/10 px-3 py-1 text-xs font-medium text-background-100"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2.5 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-300">导演</dt>
                  <dd className="text-background-50">{item.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-300">主演</dt>
                  <dd className="text-background-50">{item.cast.join(" / ")}</dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap gap-3">
                <ShareButton />
                <Link
                  to={`/category/${item.type}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-apps-2-line"></i>
                  更多{typeText}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-8xl px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
                <i className="ri-book-open-line text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{item.synopsis}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                作为一部备受关注的{item.genres.join("、")}
                {typeText}，《{item.title}》凭借扎实的制作与细腻的表达收获了不少好评。无论你偏爱
                {item.genres[0]}题材，还是想找一部适合深夜安静观看的作品，它都值得加入你的片单。
              </p>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
                  <i className="ri-image-2-line text-primary-500"></i>
                  海报图集
                </h2>
                <span className="text-xs text-foreground-400">点击图片可放大查看</span>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {gallery.map((src, i) => (
                  <button
                    key={`${src}-${i}`}
                    type="button"
                    onClick={() => setLightbox(i)}
                    className="group relative aspect-[16/10] cursor-pointer overflow-hidden rounded-md bg-background-200"
                  >
                    <img
                      src={src}
                      alt={`${item.title} 图集 ${i + 1}`}
                      title={`${item.title} - 金桔影院图集`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-background-950/0 transition-colors group-hover:bg-background-950/40">
                      <i className="ri-zoom-in-line text-2xl text-background-50 opacity-0 transition-opacity group-hover:opacity-100"></i>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
                <i className="ri-information-line text-primary-500"></i>
                作品信息
              </h2>
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
                {[
                  { label: "片名", value: item.title },
                  { label: "类型", value: typeText },
                  { label: "题材", value: item.genres.join(" / ") },
                  { label: "地区", value: item.region },
                  { label: "上映年份", value: String(item.year) },
                  { label: "片长 / 集数", value: item.duration },
                  { label: "导演", value: item.director },
                  { label: "评分", value: `${item.rating.toFixed(1)} 分` },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3 border-b border-background-100 pb-2.5">
                    <dt className="w-24 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="font-medium text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-link-m"></i>
                所属分类
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`/category/${item.type}`}
                  className="rounded-full bg-primary-100 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-200"
                >
                  {typeText}频道
                </Link>
                {item.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${item.type}`}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
              <div className="mt-5 border-t border-background-100 pt-4">
                <p className="text-xs leading-relaxed text-foreground-500">
                  本站所有影视内容均为演示数据，仅用于展示推荐与详情页面效果，不提供任何在线播放与下载服务。
                </p>
              </div>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                <i className="ri-magic-line text-lg"></i>
              </span>
              相关推荐
            </h2>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((rel) => (
                <MovieCard key={rel.id} item={rel} />
              ))}
            </div>
          </div>
        )}
      </section>

      {lightbox !== null && (
        <Lightbox
          images={gallery}
          index={lightbox}
          title={item.title}
          onClose={() => setLightbox(null)}
          onNav={(next) => setLightbox(next)}
        />
      )}
    </article>
  );
}